import React, {
  FC, useEffect, useMemo, useRef,
} from 'react';
import { FREQUENCY_AUTO_ADVANCE_DELAY_MS } from '../../data/constants';
import type { QuestionProps } from './types';

export const SelfReportQuestion: FC<QuestionProps> = ({
  question, answers, setAnswers, onComplete,
}) => {
  const base = answers[question.id] || {};
  const updateAnswerField = (k: string, v: any) => setAnswers((p) => ({
    ...p,
    [question.id]: { ...(p[question.id] || {}), [k]: v },
  }));
  const previousFrequency = useRef(base.freq);

  const triggerValues = useMemo(
    () => question.conditionalFollowUp?.triggerValues || [],
    [question.conditionalFollowUp?.triggerValues],
  );
  const followUpFields = question.conditionalFollowUp?.fields || [];
  const isTriggered = triggerValues.includes(base.freq)
    || triggerValues.includes(Number(base.freq));

  useEffect(() => {
    if (base.freq && base.freq !== previousFrequency.current) {
      previousFrequency.current = base.freq;
      const triggered = triggerValues.includes(base.freq)
        || triggerValues.includes(Number(base.freq));
      if (!triggered) {
        const t = setTimeout(onComplete, FREQUENCY_AUTO_ADVANCE_DELAY_MS);
        return () => clearTimeout(t);
      }
    }
    previousFrequency.current = base.freq;
    return undefined;
  }, [base.freq, onComplete, triggerValues]);

  const options = question.options || [];

  return (
    <div>
      <p className="text-base font-bold text-slate-800 mb-6">
        {question.text}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </p>
      <div className="flex flex-col gap-3 mb-5">
        {options.map((o) => {
          const optValue = String(o.value);
          const sel = base.freq === optValue;
          return (
            <button
              key={optValue}
              type="button"
              onClick={() => updateAnswerField('freq', optValue)}
              className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-sm font-medium text-left transition-all ${
                sel
                  ? 'border-brand bg-brand bg-opacity-5 text-brand'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  sel ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
                }`}
              >
                {sel && (
                  <span className="w-2 h-2 rounded-full bg-white block" />
                )}
              </span>
              {o.label}
            </button>
          );
        })}
      </div>
      {isTriggered && followUpFields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {followUpFields.map((field) => (
            <div key={field.id}>
              <p className="text-xs font-semibold text-slate-500 mb-1">
                {field.label}
              </p>
              <textarea
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none resize-y min-h-16"
                value={base[field.id] || ''}
                onChange={(e) => updateAnswerField(field.id, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelfReportQuestion;
